import { useEffect, useRef, useState } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  FloatingFocusManager,
  useListNavigation,
  useTypeahead,
} from '@floating-ui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreVerticalIcon, ArrowRight01Icon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import type { IconSvgElement } from '@hugeicons/react';
import type { RowAction, SubMenuItem } from './DataTableTypes';

interface RowActionMenuProps<T> {
  item: T;
  actions: RowAction<T>[];
  label: string;
  /**
   * Trigger overrides for a menu that is not a table row's kebab: the
   * Issues page's split "Capture Issues" button (CaptureIssuesButton) hangs
   * its caret on this menu. Absent, the trigger is the kebab it always was.
   */
  triggerIcon?: IconSvgElement;
  triggerClassName?: string;
  triggerDisabled?: boolean;
}

const DEFAULT_TRIGGER_CLASS = 'p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600';

export function RowActionMenu<T>({ item, actions, label, triggerIcon, triggerClassName, triggerDisabled }: RowActionMenuProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const focusSubmenu = useRef(false);
  const visibleActions = actions.filter(action => !action.hidden?.(item));
  labelsRef.current = visibleActions.map(action => action.disabled?.(item) ? null : action.label);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) { setActiveSubmenu(null); setActiveIndex(null); }
    },
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const navigation = useListNavigation(context, { listRef, activeIndex, onNavigate: setActiveIndex, loop: true, focusItemOnOpen: true });
  const typeahead = useTypeahead(context, { listRef: labelsRef, activeIndex, onMatch: setActiveIndex });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, navigation, typeahead]);

  useEffect(() => {
    if (!activeSubmenu || !focusSubmenu.current) return;
    submenuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    focusSubmenu.current = false;
  }, [activeSubmenu]);

  const closeSubmenu = () => {
    setActiveSubmenu(null);
    if (activeIndex !== null) listRef.current[activeIndex]?.focus();
  };

  const handleSubmenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); closeSubmenu(); return;
    }
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    let next = current;
    if (event.key === 'ArrowDown') next = (current + 1) % buttons.length;
    else if (event.key === 'ArrowUp') next = (current - 1 + buttons.length) % buttons.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else if (event.key.length === 1 && event.key !== ' ') next = buttons.findIndex(button => button.textContent?.trim().toLowerCase().startsWith(event.key.toLowerCase()));
    else return;
    event.preventDefault(); event.stopPropagation(); buttons[next]?.focus();
  };

  const handleAction = (action: RowAction<T>, keyboard: boolean) => {
    const submenuItems = action.getSubmenu?.(item) ?? action.submenu;
    if (submenuItems) {
      focusSubmenu.current = keyboard;
      setActiveSubmenu(activeSubmenu === action.label ? null : action.label);
      return;
    }
    action.onClick(item);
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  const handleSubmenuClick = (subItem: SubMenuItem) => {
    if (subItem.disabled) return;
    subItem.onClick();
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  /**
   * Hover is the primary trigger, matching the two sibling surfaces:
   * `.oq-project-issues__submenu-wrapper:hover` (extension) and
   * `<BulkSubmenu onHover>` (review-app). Entering any item's wrapper resolves
   * the whole submenu state, so moving onto `Delete` closes an open flyout.
   * The flyout is a DOM child of the wrapper, so travelling into it re-enters
   * the same wrapper and never flickers shut. No timers, no mouseleave.
   */
  const handleItemHover = (action: RowAction<T>, hasSubmenu: boolean, isDisabled: boolean) => {
    setActiveSubmenu(hasSubmenu && !isDisabled ? action.label : null);
  };

  /** Keyboard users get the same submenu via ArrowRight/ArrowLeft. */
  const handleItemKeyDown = (
    e: React.KeyboardEvent,
    action: RowAction<T>,
    hasSubmenu: boolean,
    isDisabled: boolean,
  ) => {
    if (!hasSubmenu || isDisabled) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusSubmenu.current = true;
      setActiveSubmenu(action.label);
      if (activeSubmenu === action.label) submenuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    } else if (e.key === 'ArrowLeft' && activeSubmenu === action.label) {
      e.preventDefault();
      setActiveSubmenu(null);
    }
  };

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        })}
        className={triggerClassName ?? DEFAULT_TRIGGER_CLASS}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={label}
        disabled={triggerDisabled}
      >
        <HugeiconsIcon icon={triggerIcon ?? MoreVerticalIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" aria-hidden="true" />
      </button>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[180px]"
            role="menu"
          >
            {visibleActions.map((action, i) => {
              const isDisabled = action.disabled?.(item) ?? false;
              const showDivider = action.destructive && i > 0;
              const submenuItems = action.getSubmenu?.(item) ?? action.submenu;
              const hasSubmenu = !!submenuItems;

              const isSubmenuOpen = hasSubmenu && activeSubmenu === action.label;

              return (
                <div
                  key={action.label}
                  className="relative"
                  onMouseEnter={() => handleItemHover(action, hasSubmenu, isDisabled)}
                >
                  {showDivider && <div className="border-t border-gray-200 my-1" />}
                  <button
                    ref={node => { listRef.current[i] = node; }}
                    tabIndex={activeIndex === i ? 0 : -1}
                    {...getItemProps({ onFocus: () => setActiveIndex(i) })}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between whitespace-nowrap ${
                      isDisabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : action.destructive
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    role="menuitem"
                    onClick={(event) => !isDisabled && handleAction(action, event.detail === 0)}
                    onKeyDown={(e) => handleItemKeyDown(e, action, hasSubmenu, isDisabled)}
                    disabled={isDisabled}
                    title={
                      isDisabled
                        ? (typeof action.disabledTitle === 'function'
                            ? action.disabledTitle(item)
                            : action.disabledTitle)
                        : undefined
                    }
                    aria-haspopup={hasSubmenu ? 'menu' : undefined}
                    aria-expanded={hasSubmenu ? isSubmenuOpen : undefined}
                  >
                    <span>{action.label}</span>
                    {hasSubmenu && (
                      <HugeiconsIcon icon={ArrowRight01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
                    )}
                  </button>

                  {submenuItems && isSubmenuOpen && (
                    // Opens LEFTWARD (`right-full`): the actions menu is right-aligned
                    // at the table's edge, so a rightward flyout renders off-screen.
                    // Mirrors the extension (`_project-issues.css` .oq-project-issues__submenu)
                    // and the review-app (`BulkSubmenu`), which both use `right: 100%`.
                    <div
                      ref={submenuRef}
                      onKeyDown={handleSubmenuKeyDown}
                      className="absolute right-full top-0 mr-1 min-w-[150px] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                      role="menu"
                    >
                      {submenuItems.map((subItem) => (
                        <button
                          key={subItem.label}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 whitespace-nowrap ${
                            subItem.disabled
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => handleSubmenuClick(subItem)}
                          disabled={subItem.disabled}
                          title={subItem.disabled ? subItem.title : undefined}
                        >
                          {subItem.checked ? (
                            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 text-primary-600" aria-hidden="true" />
                          ) : (
                            <span className="w-3.5" />
                          )}
                          <span className="text-cap-trim">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
